import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { useLazyQuery } from '@apollo/client'
import { Alert, Box, Button, useToggle } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  AllowanceFragment,
  DeleteAllowanceDocument,
  DeleteAllowanceMutation,
  FetchAllowanceIncidencesCountDocument,
  FetchPolicyAllowancesDocument,
  UpdateAllowancePositionDocument,
  UpdateAllowancePositionMutation,
} from 'generated/resources/graphql'
import { compact, debounce } from 'lodash'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import 'modules/core/components/Popover/Content'
import type { Option } from 'modules/core/components/Popover/Content'
import SortableBoxList from 'modules/core/components/SortableBoxList'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import AllowanceSelector from 'modules/timeoff/components/TimeoffAllowances/AllowanceSelector'

import AllowanceItem from './AllowanceItem'
import { getAllowanceSubtitle } from './getAllowanceSubtitle'

class DeleteForm extends Form {
  constructor(values: Record<string, unknown>) {
    super(values, {
      alt_allowance_id: 'number',
    })
  }
}

interface Props {
  policyId: number
}

interface AllowancesOption {
  label: string
  value: number
}

const TimeoffAllowances = ({ policyId }: Props) => {
  const { navigation } = useNavigation()

  const navigate = useNavigate()
  const form = React.useRef(new DeleteForm({})).current
  const [allowancesOptions, setAllowancesOptions] = React.useState<AllowancesOption[]>([])
  const [deleteModal, toggleDeleteModal] = useToggle(false)
  const [allowanceToDelete, setAllowanceToDelete] = React.useState<AllowanceFragment | null>(null)
  const [submitting, setSubmitting] = React.useState<boolean>(false)
  const [hasDifferentTypeSelected, setHasDifferentTypeSelected] = React.useState<boolean>(false)
  const { data, loading } = useQuery(FetchPolicyAllowancesDocument, {
    variables: {
      policyId: policyId,
    },
    fetchPolicy: 'network-only',
  })

  const [getIncidences, { loading: loadingIncidences, data: incidences }] = useLazyQuery(
    FetchAllowanceIncidencesCountDocument
  )

  const [allowances, setAllowances] = React.useState<AllowanceFragment[]>([])

  const sortAllowances = (allowancesInput: AllowanceFragment[]) => {
    return [...allowancesInput].sort((a, b) => {
      if (a.position === null && b.position === null) return 0
      if (a.position !== null && a.position !== undefined) {
        return a.position - (b.position || 0)
      } else return 1
    })
  }

  useEffect(() => {
    setAllowances(sortAllowances(data?.timeoff.allowancesConnection.nodes || []))
  }, [loading])

  const [deleteAllowanceMutation] = useMutation(DeleteAllowanceDocument, {
    extractorFn: (mutationData: DeleteAllowanceMutation) =>
      mutationData.timeoffMutations.deleteWithAltAllowanceTimeoffAllowance,
  })

  const [updateAllowancePositionMutation] = useMutation(UpdateAllowancePositionDocument, {
    extractorFn: (mutationData: UpdateAllowancePositionMutation) =>
      mutationData.timeoffMutations.updateTimeoffAllowance,
  })

  const onEdit = (allowanceId: number) => {
    const editAllowanceRoute =
      navigation.settings.timeoffPolicies.id.allowances.allowanceId.fullPath({
        id: policyId,
        allowanceId,
      })

    navigate(editAllowanceRoute)
  }

  const onToggleDelete = async (allowance: AllowanceFragment) => {
    setAllowanceToDelete(allowance)
    getIncidences({ variables: { allowanceId: allowance.id } })
    setAllowancesOptions(() =>
      allowances
        .filter((a) => a !== allowance)
        .map((a) => ({
          label: a.name,
          value: a.id,
        }))
    )
    toggleDeleteModal(true)()
  }

  const submitDestroy = () => async () => {
    setSubmitting(true)

    if (!allowanceToDelete) return

    const altAllowanceId =
      Number(form.get('alt_allowance_id').value) || Number(allowancesOptions[0]?.value)

    const response = await deleteAllowanceMutation({
      variables: {
        id: allowanceToDelete.id,
        altAllowanceId,
      },
      refetchQueries: [FetchPolicyAllowancesDocument],
      awaitRefetchQueries: true,
    })

    setSubmitting(false)

    const result = handleResult(response, (errors) => {
      form.setErrors(errors)
    })

    if (result.success) {
      setAllowanceToDelete(null)
      toggleDeleteModal(false)()
      setAllowances(allowances.filter((a) => a.id !== allowanceToDelete.id))
    }
  }

  const renderCard = (allowance: AllowanceFragment) => {
    const options: Option[] = [
      {
        title: i18n.t('time_off.allowances.edit'),
        onClick: () => onEdit(allowance.id),
      },
    ]

    if (allowances.length > 1) {
      options.push({
        title: i18n.t('time_off.allowances.delete'),
        onClick: () => onToggleDelete(allowance),
        type: 'wrong',
      })
    }

    return (
      <AllowanceItem
        key={allowance.id}
        label={allowance.name}
        subtitle={getAllowanceSubtitle(allowance)}
        popoverOptions={options}
      />
    )
  }

  const allowanceIncidencesCount = allowanceToDelete
    ? incidences?.timeoff.allowanceIncidencesConnection.totalCount
    : 0

  const description =
    allowanceIncidencesCount && allowanceIncidencesCount > 0
      ? i18n.t('time_off.allowances.delete_modal.description_with_incidences')
      : i18n.t('time_off.allowances.delete_modal.description')

  const handleMoveIncidences = (value: boolean) => {
    setHasDifferentTypeSelected(value)
  }

  const findAllowance = (allowanceId: number) => allowances.find((a) => a.id === allowanceId)

  const updatePosition = async (items: AllowanceFragment[]) => {
    const promises = compact(
      items.map((allowance, key) => {
        const resource = findAllowance(allowance.id)

        if (resource) {
          return updateAllowancePositionMutation({
            variables: {
              id: resource.id,
              position: key,
            },
          })
        }
      })
    )
    await Promise.all(promises)
    setAllowances(items)
  }

  const debouncedUpdatePosition = debounce(
    (items: AllowanceFragment[]) => updatePosition(items),
    1000
  )

  // Trigger updatePosition if component gets unmounted
  useEffect(
    () => () => {
      debouncedUpdatePosition.flush()
    },
    []
  )

  if (loading) return <Loading />

  const isDisabled = (_item: AllowanceFragment) => allowances.length < 2

  return (
    <>
      <SortableBoxList
        items={allowances}
        isDisabled={isDisabled}
        emptyMessage="emptyMessage"
        onSortEnd={debouncedUpdatePosition}
        itemAsKey={(item) => item.id.toString()}
        renderItem={(allowance) => renderCard(allowance)}
      />
      {deleteModal && (
        <Modal
          illustration="delete-cross"
          title={i18n.t('time_off.allowances.delete_modal.title', {
            name: allowanceToDelete ? allowanceToDelete.name : '...',
          })}
          description={description}
          onClose={toggleDeleteModal(false)}
          footer={
            <Button
              type="negative"
              onClick={submitDestroy()}
              disabled={
                loadingIncidences ||
                submitting ||
                (!!allowanceIncidencesCount && !form.get('alt_allowance_id').value) ||
                hasDifferentTypeSelected
              }
            >
              {i18n.t('time_off.allowances.delete_modal.button')}
            </Button>
          }
        >
          {allowanceIncidencesCount && allowanceIncidencesCount > 0 && (
            <Box gap="s12">
              <AllowanceSelector
                field={form.get('alt_allowance_id')}
                allowances={allowances}
                allowanceToDelete={allowanceToDelete}
                handleMoveIncidences={handleMoveIncidences}
              />
              {hasDifferentTypeSelected && (
                <Alert color="warning">
                  {i18n.t('time_off.allowances.delete_modal.different_type_warning')}
                </Alert>
              )}
            </Box>
          )}
        </Modal>
      )}
    </>
  )
}

export default observer(TimeoffAllowances)
